import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, Loader2, Save, Plus, X as XIcon, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth, isAdmin } from "../contexts/AuthContext";
import { BADGES_EVENT } from "./PlayerBadges";
import BadgeEmblem, { GOLD, badgeTheme } from "./BadgeEmblem";

const CATEGORY_LABEL = { DNA: "DNA Badges", "On Court": "On Court Badges", Physical: "Physical Badges" };

// Bottom of the Development Inventory — coach/trainer picks which catalog badges the player has earned.
export default function PlayerBadgesEditor({ playerId }) {
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  const admin = isAdmin(user);
  const [catalog, setCatalog] = useState({ categories: [], badges: [] });
  const [granted, setGranted] = useState([]);
  const [draft, setDraft] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCat, setNewCat] = useState("On Court");
  const [manage, setManage] = useState(false);

  useEffect(() => {
    let live = true;
    Promise.all([api.listBadges(), api.getPlayerBadges(playerId)]).then(([c, g]) => {
      if (!live) return;
      setCatalog(c); setGranted(g.badges || []); setDraft(g.badges || []); setMeta({ updated_at: g.updated_at, updated_by: g.updated_by });
    }).catch(() => toast.error("Could not load badges")).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [playerId]);

  const dirty = JSON.stringify([...draft].sort()) !== JSON.stringify([...granted].sort());
  const toggle = (name) => canEdit && setDraft(d => d.includes(name) ? d.filter(x => x !== name) : [...d, name]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.setPlayerBadges(playerId, draft);
      setGranted(r.badges); setDraft(r.badges); setMeta({ updated_at: r.updated_at, updated_by: r.updated_by });
      window.dispatchEvent(new CustomEvent(BADGES_EVENT, { detail: { playerId, badges: r.badges } }));
      toast.success(`${r.badges.length} badge${r.badges.length === 1 ? "" : "s"} on the profile`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const addToCatalog = async () => {
    if (!newName.trim()) return;
    try {
      const b = await api.addBadge({ name: newName.trim(), category: newCat });
      setCatalog(c => ({ ...c, badges: [...c.badges, b] }));
      setNewName("");
      toast.success(`"${b.name}" added to the badge library`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add badge"); }
  };

  const removeFromCatalog = async (b) => {
    if (!window.confirm(`Remove "${b.name}" from the badge library? It will be pulled from every player who has it.`)) return;
    try {
      await api.deleteBadge(b.id);
      setCatalog(c => ({ ...c, badges: c.badges.filter(x => x.id !== b.id) }));
      setDraft(d => d.filter(x => x !== b.name)); setGranted(g => g.filter(x => x !== b.name));
    } catch { toast.error("Could not remove badge"); }
  };

  return (
    <section className="border bg-gradient-to-br from-[#1c1c1c] to-[#161616] p-6 lg:p-8" style={{ borderColor: `${GOLD}55` }} data-testid="player-badges-editor">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <Award className="w-5 h-5" style={{ color: GOLD }} />
            <h2 className="font-heading uppercase text-2xl lg:text-3xl tracking-[0.15em] text-white">Player Badges</h2>
          </div>
          <div className="text-[11px] italic text-[#a0a0a0] mt-1">To be filled out by Trainer / Coach · granted badges show on the player's profile hero</div>
          {meta.updated_at && (
            <div className="mt-2 text-[10px] uppercase tracking-widest text-[#757575]" data-testid="badges-updated">
              Updated · {new Date(meta.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}{meta.updated_by ? ` · ${meta.updated_by}` : ""}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#757575]" data-testid="badges-count">{draft.length} granted</span>
          {admin && (
            <button onClick={() => setManage(m => !m)} className="h-9 px-3 text-[10px] uppercase tracking-widest border border-white/10 text-[#a0a0a0] hover:text-white hover:border-white/30" style={{ transition: "color .15s ease, border-color .15s ease" }} data-testid="badges-manage-toggle">
              {manage ? "Done" : "Manage library"}
            </button>
          )}
          {canEdit && (
            <button onClick={save} disabled={!dirty || saving} className="flex items-center gap-2 h-9 px-4 text-xs uppercase tracking-[0.25em] font-bold text-[#121212] disabled:opacity-40" style={{ background: GOLD, transition: "opacity .15s ease" }} data-testid="badges-save-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Badges
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: GOLD }} /></div>
      ) : (
        <div className="mt-6 space-y-5">
          {catalog.categories.map(cat => {
            const items = catalog.badges.filter(b => b.category === cat);
            if (!items.length) return null;
            const accent = badgeTheme(cat).color;
            return (
              <div key={cat} data-testid={`badges-group-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center gap-2.5 mb-2.5">
                  <BadgeEmblem category={cat} size={26} glow={false} />
                  <div className="text-[10px] uppercase tracking-[0.35em] font-bold" style={{ color: accent }}>{CATEGORY_LABEL[cat] || cat}</div>
                  <span className="flex-1 h-px" style={{ background: `${accent}33` }} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {items.map(b => {
                    const on = draft.includes(b.name);
                    return (
                      <span key={b.id} className="inline-flex items-stretch">
                        <button type="button" onClick={() => toggle(b.name)} disabled={!canEdit}
                          className={`inline-flex items-center gap-1.5 h-8 px-3 border text-[11px] uppercase tracking-[0.12em] font-bold ${on ? "text-[#121212]" : "text-[#a0a0a0] border-white/15 hover:text-white hover:border-white/40"} disabled:cursor-default`}
                          style={{ ...(on ? { background: accent, borderColor: accent, boxShadow: `0 0 12px ${accent}44` } : {}), transition: "background-color .15s ease, color .15s ease, border-color .15s ease, box-shadow .15s ease" }}
                          data-testid={`badge-opt-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          {on ? <CheckCircle2 className="w-3 h-3" /> : <Award className="w-3 h-3" />} {b.name}
                        </button>
                        {manage && (
                          <button onClick={() => removeFromCatalog(b)} className="px-1.5 border border-l-0 border-white/15 text-[#757575] hover:text-red-400" data-testid={`badge-delete-${b.id}`} title="Remove from library">
                            <XIcon className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {manage && (
            <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-white/10" data-testid="badges-add-row">
              <select value={newCat} onChange={e => setNewCat(e.target.value)} className="h-9 px-3 bg-[#101010] border border-white/15 text-xs uppercase tracking-widest text-white focus:outline-none" data-testid="badge-new-category">
                {catalog.categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === "Enter" && addToCatalog()} placeholder="New badge name…"
                className="h-9 px-3 min-w-[220px] bg-[#101010] border border-white/15 text-sm text-white focus:outline-none" style={{ borderColor: newName ? `${GOLD}88` : undefined }} data-testid="badge-new-name" />
              <button onClick={addToCatalog} disabled={!newName.trim()} className="flex items-center gap-1.5 h-9 px-3 text-[10px] uppercase tracking-widest font-bold border disabled:opacity-40" style={{ color: GOLD, borderColor: `${GOLD}66` }} data-testid="badge-new-add">
                <Plus className="w-3 h-3" /> Add to library
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
